import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styles from './ProfilePage.module.css';
import ProfileHeader from '../../components/ProfileHeader/ProfileHeader';
import InfoView from '../../components/InfoView/InfoView';
import ChannelCard from '../../components/ChannelCard/ChannelCard';
import ProfileBlocksView from '../../components/ProfileBlocksView/ProfileBlocksView';
import ProfileTableView from '../../components/ProfileTableView/ProfileTableView';
import RevealHiddenModal from '../../components/RevealHiddenModal/RevealHiddenModal';
import useChannels from '../../hooks/useChannels';
import useRevealHidden from '../../hooks/useRevealHidden';
import EmptyState from '../../components/EmptyState/EmptyState';
import useAuth from '../../hooks/useAuth';

export default function ProfilePage({ handle: propHandle }) {
  const { handle: paramHandle } = useParams();
  const handle = propHandle || paramHandle;
  const [activeView, setActiveView] = useState('channels');
  const [activeOrder, setActiveOrder] = useState('newest');
  const { session } = useAuth();
  const { profile, channels, isOwner, loading, lastModified } = useChannels(handle);
  const { revealedChannels, revealHidden } = useRevealHidden(handle);
  const [showRevealModal, setShowRevealModal] = useState(false);

  const allChannels = useMemo(() => {
    if (!revealedChannels.length) return channels;
    const existingIds = new Set(channels.map(c => c.id));
    const newRevealed = revealedChannels.filter(c => !existingIds.has(c.id));
    return [...channels, ...newRevealed];
  }, [channels, revealedChannels]);

  if (loading) {
    return (
      <main className={styles.main}>
        <div className={styles.skeleton}>
          <div className={styles.skeletonHeader} />
          <div className={styles.skeletonRow} style={{ width: '60%' }} />
          <div className={styles.skeletonRow} style={{ width: '40%' }} />
          <div className={styles.skeletonCards}>
            {[0, 1, 2].map(i => <div key={i} className={styles.skeletonCard} />)}
          </div>
        </div>
      </main>
    );
  }

  if (!profile) {
    return (
      <main className={styles.main}>
        <div className={styles.notFound}>Profile not found</div>
      </main>
    );
  }

  const ownerSession = isOwner ? session : null;

  return (
    <main className={styles.main}>
      <ProfileHeader
        profile={profile}
        isOwner={isOwner}
        onRevealClick={!isOwner ? () => setShowRevealModal(true) : undefined}
      />
      <InfoView
        profile={profile}
        activeView={activeView}
        onViewChange={setActiveView}
        activeOrder={activeOrder}
        onOrderChange={setActiveOrder}
        session={ownerSession}
        lastModified={lastModified}
      />
      <div className={styles.contentArea}>
        {activeView === 'channels' && (
          allChannels.length === 0
            ? <EmptyState message={isOwner ? "Create your first channel from the navigation bar." : "No public channels yet."} />
            : <section className={styles.channels}>
                {allChannels.map(channel => (
                  <ChannelCard key={channel.id} channel={channel} />
                ))}
              </section>
        )}
        {activeView === 'blocks' && <ProfileBlocksView order={activeOrder} handle={handle} />}
        {activeView === 'table' && <ProfileTableView channels={channels} order={activeOrder} />}
      </div>
      {showRevealModal && (
        <RevealHiddenModal
          onReveal={revealHidden}
          onClose={() => setShowRevealModal(false)}
        />
      )}
    </main>
  );
}
